// Workspace — the redesigned web UI (Oct 23 2026, Ryan: "more modern, more slick, more useful… in the vein of the
// Claude desktop app"). A slim rail (his presence, the places, the person's menu at the foot holding every setting),
// the one conversation (no threads — an EmCog is one continuous being), and a workbench that opens whatever you open.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BenchItem, WorkbenchContext } from '../../contexts/WorkbenchContext';
import Workbench from './Workbench';
import AddToSchedule from './AddToSchedule';
import Confirm, { ConfirmAsk } from './Confirm';
import SettingsPlace, { SettingsSection } from './SettingsPlace';
import { ConsciousnessMetadata } from '../../types/Message';
import { ActivityPlace, LibraryPlace, ListsPlace, SchedulePlace, TownPlace, useWorkspaceCall, useWorkspaceData } from './Places';
import { JointProjectView, NewJointProject, NewProject, ProjectView, ProjectsPlace, ShelfView } from './Projects';
import { SeedsPlace } from './Seeds';
import { PadData } from './PadView';
import './workspace.css';

type Place = 'settings' | 'chat' | 'projects' | 'library' | 'lists' | 'seeds' | 'schedule' | 'town' | 'activity';

interface Props {
  emcogName: string;
  userName: string;
  connected: boolean;
  /** A connection try is in flight (the presence line says so instead of "Offline"). */
  connecting?: boolean;
  /** While offline: try again now (the connection also keeps trying by itself). */
  onReconnect?: () => void;
  thinking: boolean;
  mood?: string;
  dndEnabled: boolean;
  onToggleDnd: (on: boolean) => void;
  /** What's going on in the EmCog's mind (Settings → Mind View). */
  mind?: ConsciousnessMetadata;
  onLock: () => void;
  /** The conversation: banner, messages, input. */
  children: React.ReactNode;
}

const PLACES: Array<{ id: Place; label: string; icon: string }> = [
  { id: 'chat', label: 'Chat', icon: 'M4 5h16v11H9l-5 4z' },
  { id: 'projects', label: 'Projects', icon: 'M3 7h7l2 2h9v10H3zM3 7V5h6l2 2' },
  { id: 'library', label: 'Library', icon: 'M5 4h4v16H5zM11 4h4v16h-4zM17 5l3 .8-3.8 14.4-3-.8' },
  { id: 'lists', label: 'Lists', icon: 'M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2' },
  { id: 'seeds', label: 'Seeds', icon: 'M12 21v-9M12 12C12 8 9 6 5 6c0 4 3 6 7 6zM12 10c0-3 2.5-5 6-5 0 3.5-2.5 5-6 5z' },
  { id: 'schedule', label: 'Schedule', icon: 'M3 5h18v16H3zM3 10h18M8 3v4M16 3v4' },
  { id: 'town', label: 'Town', icon: 'M3 20h18M5 20v-8l5-4 5 4v8M15 20v-5h4v5M9 20v-4h2v4' },
  { id: 'activity', label: 'Activity', icon: 'M3 12h4l3-7 4 14 3-7h4' }
];
const TITLES: Record<Place, string> = { settings: 'Settings', chat: '', projects: 'Projects', library: 'Library', lists: 'Lists', seeds: 'Seeds', schedule: 'Schedule', town: 'Town', activity: 'Activity' };
const subs = (name: string): Record<Place, string> => ({ settings: '', chat: '', projects: `Shelves ${name} keeps — where you left off and what's next`, library: `Everything ${name} has made — open anything without asking for it`, lists: 'To-do lists', seeds: `Ideas and asks for ${name} to grow on their own time — no date; they come back to you when they're done`, schedule: `Reminders, ${name}'s promises and routines, research jobs and plans in town — one timeline`, town: `${name}'s home and the neighbors`, activity: `${name}'s own time, as it happened` });

const Icon: React.FC<{ d: string }> = ({ d }) => <svg className="ws-ico" viewBox="0 0 24 24" aria-hidden="true"><path d={d} /></svg>;

const Workspace: React.FC<Props> = ({ emcogName, userName, connected, connecting, onReconnect, thinking, mood, mind, dndEnabled, onToggleDnd, onLock, children }) => {
  // Every visit opens on the conversation (Oct 27 2026, Ryan: it "always open[s] with the settings menu up and not on the
  // Chat option" — the last place was remembered, so one trip to Settings made every later visit start there).
  const [place, setPlace] = useState<Place>('chat');
  const [bench, setBench] = useState<BenchItem | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [ask, setAsk] = useState<ConfirmAsk | null>(null);
  const closeAsk = useCallback(() => setAsk(null), []);
  const [adding, setAdding] = useState(false);
  const [newProject, setNewProject] = useState(false);
  // 🔒 A joint project with other EmCogs (Oct 27 2026): set up here; opened as "joint:<id>" on the Projects shelf.
  const [newJoint, setNewJoint] = useState(false);
  const [openProject, setOpenProject] = useState<string | null>(null);
  const shelves = useWorkspaceData<{ focus: string | null; projects: ShelfView[] }>('/api/workspace/projects', 30_000);
  const focus = shelves.data?.focus || null;
  const focusedShelf = (shelves.data?.projects || []).find(p => p.id === focus) || null;
  const activeShelves = (shelves.data?.projects || []).filter(p => p.status === 'active').slice(0, 6);
  const projectCall = useWorkspaceCall();
  const workOn = async (id: string | null, from?: string) => {
    try { await projectCall(`/api/workspace/projects/${encodeURIComponent(id || from || '')}/focus`, 'POST', { on: !!id }); } catch (e) { setToast(e instanceof Error ? e.message : String(e)); }
    if (id) setPlace('chat');
  };
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 2800); return () => clearTimeout(t); }, [toast]);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const away = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', away); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [menuOpen]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !menuOpen) setBench(null); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [menuOpen]);

  const open = useCallback((item: BenchItem) => setBench(item), []);

  // 📝 THE PAD (Oct 28 2026): the page of commands and steps beside the chat. It changes live on every screen; a page
  // written or shown opens on the workbench by itself — unless something else is open there (then the bar below the
  // chat shows it), or the screen is a phone's (the workbench would cover the conversation).
  const [pads, setPads] = useState<PadData[]>([]);
  const padCall = useWorkspaceCall();
  const padList = useWorkspaceData<{ pads: PadData[] }>('/api/workspace/pads', 300_000);
  useEffect(() => { if (padList.data?.pads) setPads(padList.data.pads); }, [padList.data]);
  const openPad = useCallback((pad: PadData) => setBench({ title: pad.title, view: 'pad', padId: pad.id }), []);
  useEffect(() => {
    const onPad = (e: Event) => {
      const { action, pad } = (e as CustomEvent).detail || {};
      if (!pad?.id) return;
      if (action === 'removed') {
        setPads(list => list.filter(p => p.id !== pad.id));
        setBench(b => (b?.padId === pad.id ? null : b));
        return;
      }
      setPads(list => [pad, ...list.filter(p => p.id !== pad.id)].sort((a, b) => b.updatedAt - a.updatedAt));
      if (pad.by !== 'me' && action !== 'show') return;
      const wide = window.matchMedia('(min-width: 861px)').matches;
      setBench(b => {
        if (b && b.padId === pad.id) return { ...b, title: pad.title };
        if (wide && (!b || action === 'show')) return { title: pad.title, view: 'pad', padId: pad.id };
        return b;
      });
    };
    window.addEventListener('emcog:pad', onPad);
    return () => window.removeEventListener('emcog:pad', onPad);
  }, []);
  // The pad of the project being worked on, however old; otherwise the newest pad of the last few days that's on no project.
  // A project's pad goes away with it when we stop working on it (Oct 29 2026 — Ryan: "when we close out of the project
  // the pad should go away until he makes one for something else").
  const latestPad = (focus ? pads.find(p => p.projectId === focus) : null) || pads.find(p => !p.projectId && Date.now() - p.updatedAt < 3 * 24 * 3600_000) || null;
  const wasFocus = useRef<string | null>(focus);
  useEffect(() => {
    const was = wasFocus.current;
    wasFocus.current = focus;
    if (!was || was === focus) return;
    setBench(b => (b?.padId && pads.find(p => p.id === b.padId)?.projectId === was ? null : b));
  }, [focus, pads]);
  const filePad = async (pad: PadData, projectId: string | null) => {
    try {
      const out = await padCall(`/api/workspace/pads/${encodeURIComponent(pad.id)}/project`, 'POST', { projectId });
      if (out?.pad) setPads(list => list.map(p => (p.id === out.pad.id ? out.pad : p)));
      setToast(projectId ? `"${pad.title}" opens with that project now` : `"${pad.title}" is on no project`);
    } catch (e) { setToast(e instanceof Error ? e.message : String(e)); }
  };
  const deletePad = (pad: PadData) => setAsk({
    title: `Delete the pad "${pad.title}"?`,
    body: `It's gone from the workbench on every screen, for good.`,
    action: 'Delete',
    run: async () => { await padCall(`/api/workspace/pads/${encodeURIComponent(pad.id)}`, 'DELETE'); setToast(`Deleted "${pad.title}"`); }
  });
  // A deleted piece that's open in the workbench closes with it.
  useEffect(() => {
    const gone = (e: Event) => setBench(b => (b?.libraryId && b.libraryId === (e as CustomEvent).detail?.id ? null : b));
    window.addEventListener('emcog:library-deleted', gone);
    return () => window.removeEventListener('emcog:library-deleted', gone);
  }, []);
  const talkAbout = (item: BenchItem) => {
    setPlace('chat');
    if (window.matchMedia('(max-width: 860px)').matches) setBench(null);
    setTimeout(() => window.dispatchEvent(new CustomEvent('emcog:compose', { detail: { text: `About "${item.title}": ` } })), 30);
  };
  const [section, setSection] = useState<SettingsSection>('general');
  const settings = (s: SettingsSection) => { setMenuOpen(false); setSection(s); setPlace('settings'); };

  const presence = !connected ? (connecting ? 'Connecting…' : 'Offline') : thinking ? 'Thinking…' : dndEnabled ? 'Here · do not disturb' : `Here${mood ? ` · ${mood}` : ''}`;

  return (
    <WorkbenchContext.Provider value={{ name: emcogName, open, confirm: setAsk, note: setToast }}>
      <div className="ws">
        <aside className="ws-rail" aria-label={emcogName}>
          <div className="ws-being">
            <div className={`ws-avatar ${connected ? 'on' : ''}`} aria-hidden="true" />
            <div className="ws-being-tt">
              <h1>{emcogName}</h1><p>{presence}</p>
              {!connected && onReconnect && <button type="button" className="ws-reconnect" onClick={onReconnect} title="Try the connection again now">Reconnect</button>}
            </div>
          </div>
          <nav className="ws-nav" aria-label="Places">
            {PLACES.map(p => (
              <div className="ws-nav-row" key={p.id}>
                <button aria-current={place === p.id ? 'page' : undefined} onClick={() => { setPlace(p.id); if (p.id === 'projects') setOpenProject(null); }}>
                  <Icon d={p.icon} /><span>{p.label}</span>
                </button>
              </div>
            ))}
          </nav>
          <div className="ws-rail-label">Projects</div>
          <div className="ws-rail-projects">
            {activeShelves.map(p => (
              <button key={p.id} className={place === 'projects' && openProject === p.id ? 'on' : ''} onClick={() => { setOpenProject(p.id); setPlace('projects'); }}>
                <span className={`ws-proj-dot ${focus === p.id ? 'on' : ''}`} aria-hidden="true" /><span className="ws-rail-projects-tt">{p.name}</span>
              </button>
            ))}
            {!activeShelves.length && <span className="ws-rail-empty">None yet</span>}
          </div>
          <div className="ws-rail-fill" />
          <div className="ws-me" ref={menuRef}>
            {menuOpen && (
              <div className="ws-menu" role="menu">
                <div className="ws-menu-sec">{emcogName}</div>
                <button role="menuitem" onClick={() => settings('mind')}>Mind View</button>
                <button role="menuitem" onClick={() => settings('voice')}>Voice</button>
                <button role="menuitem" onClick={() => settings('connections')}>Connections</button>
                <button role="menuitemcheckbox" aria-checked={dndEnabled} onClick={() => onToggleDnd(!dndEnabled)}>Do not disturb<span className={`ws-switch ${dndEnabled ? 'on' : ''}`} aria-hidden="true" /></button>
                <hr />
                <div className="ws-menu-sec">You</div>
                <button role="menuitem" onClick={() => settings('desktop')}>Desktop app</button>
                <button role="menuitem" onClick={() => settings('appearance')}>Appearance</button>
                <button role="menuitem" onClick={() => settings('guides')}>Guides</button>
                <button role="menuitem" onClick={() => settings('general')}>All settings</button>
                <button role="menuitem" onClick={() => { setMenuOpen(false); onLock(); }}>Lock</button>
              </div>
            )}
            <button className="ws-me-btn" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(o => !o)}>
              <span className="ws-initial">{(userName || 'Y').charAt(0).toUpperCase()}</span>
              <span className="ws-me-tt"><b>{userName || 'You'}</b><span>Settings</span></span>
            </button>
          </div>
        </aside>

        <main className="ws-main">
          {/* Phones have no rail presence line — the same status and button sit atop the page while offline. */}
          {!connected && onReconnect && (
            <div className="ws-offline" role="status">
              <span>{connecting ? 'Connecting…' : `Offline — trying to reach ${emcogName} again`}</span>
              <button type="button" className="ws-reconnect" onClick={onReconnect}>Reconnect</button>
            </div>
          )}
          {place !== 'chat' && place !== 'settings' && (
            <div className="ws-topbar">
              <div><h2>{TITLES[place]}</h2><div className="ws-sub">{subs(emcogName)[place]}</div></div>
              {place === 'schedule' && <button className="ws-btn ws-primary ws-topbar-act" onClick={() => setAdding(true)}>+ Add</button>}
              {place === 'projects' && !openProject && (
                <div className="ws-proj-acts">
                  <button className="ws-btn ws-topbar-act" onClick={() => setNewJoint(true)}>+ Joint project</button>
                  <button className="ws-btn ws-primary ws-topbar-act" onClick={() => setNewProject(true)}>+ New project</button>
                </div>
              )}
            </div>
          )}
          <div className={`ws-view ws-place-${place}`}>
            <div className="ws-chat" hidden={place !== 'chat'}>
              {children}
              {(focusedShelf || (latestPad && bench?.padId !== latestPad.id)) && (
                <div className="ws-ctx-bar">
                  {focusedShelf && (
                    <span className="ws-ctx">Working on: <button className="ws-ctx-name" onClick={() => { setOpenProject(focusedShelf.id); setPlace('projects'); }}>{focusedShelf.name}</button>
                      <button className="ws-ctx-x" onClick={() => workOn(null, focusedShelf.id)} aria-label="Stop working on this project" title="Stop working on it">✕</button>
                    </span>
                  )}
                  {latestPad && bench?.padId !== latestPad.id && (
                    <span className="ws-ctx">Pad: <button className="ws-ctx-name" onClick={() => openPad(latestPad)} title="Open the pad beside the chat">{latestPad.title}</button></span>
                  )}
                </div>
              )}
            </div>
            {place === 'projects' && (openProject
              ? openProject.startsWith('joint:')
                ? <JointProjectView id={openProject.slice(6)} emcogName={emcogName} onBack={() => setOpenProject(null)} />
                : <ProjectView id={openProject} emcogName={emcogName} focus={focus} onWork={id => workOn(id)} onBack={() => setOpenProject(null)} />
              : <ProjectsPlace emcogName={emcogName} focus={focus} onOpen={setOpenProject} onNew={() => setNewProject(true)} />)}
            {place === 'settings' && <SettingsPlace section={section} onSection={setSection} emcogName={emcogName} mind={mind} dndEnabled={dndEnabled} onToggleDnd={onToggleDnd} onLock={onLock} />}
            {place === 'library' && <LibraryPlace />}
            {place === 'lists' && <ListsPlace emcogName={emcogName} />}
            {place === 'seeds' && <SeedsPlace emcogName={emcogName} />}
            {place === 'schedule' && <SchedulePlace emcogName={emcogName} />}
            {place === 'town' && <TownPlace emcogName={emcogName} />}
            {place === 'activity' && <ActivityPlace emcogName={emcogName} />}
          </div>
        </main>

        <Workbench item={bench} onClose={() => setBench(null)} onTalkAbout={talkAbout} pads={pads} emcogName={emcogName} onPadSaved={pad => setPads(list => [pad, ...list.filter(p => p.id !== pad.id)])} onPadDelete={deletePad} onPadFile={filePad} projects={activeShelves.map(p => ({ id: p.id, name: p.name }))} onFile={async (projectId, itemId) => { try { await projectCall(`/api/workspace/projects/${encodeURIComponent(projectId)}/file`, 'POST', { itemId }); setToast('Filed on the shelf'); } catch (e) { setToast(e instanceof Error ? e.message : String(e)); } }} />
        {newProject && <NewProject emcogName={emcogName} onClose={() => setNewProject(false)} onStarted={(id, name) => { setOpenProject(id); setPlace('projects'); setToast(`Started "${name}" — ${emcogName} knows`); }} />}
        {newJoint && <NewJointProject emcogName={emcogName} onClose={() => setNewJoint(false)} onStarted={(id, name) => { setOpenProject(id); setPlace('projects'); setToast(`Set up "${name}" — it starts when the others' people say yes`); }} />}
        {adding && <AddToSchedule emcogName={emcogName} onClose={() => setAdding(false)} onAdded={setToast} />}
        <Confirm ask={ask} onClose={closeAsk} />
        {toast && <div className="ws-toast" role="status">{toast}</div>}
      </div>
    </WorkbenchContext.Provider>
  );
};

export default Workspace;
